import React from 'react';
import { useHistory, generatePath, Link } from 'react-router-dom';
import moment from 'moment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box } from '@mui/material';
import BaseMUIDataTable from '../../../../components/BaseMUIDataTable/BaseMUIDataTable';
import { getMuiTableDataIndex } from '../../../../helpers/getters';
import TableActionsButtonsWrapper from '../../../../components/TableActionsButtonsWrapper';
import TableActionButton from '../../../../components/TableActionButton';
import { formatCsvFileName } from '../../../../helpers/formatters';
import routes from '../../../../routes';
import { deepParseJSON, stringifyAndReplaceQuotes } from '../../../../helpers/logs';

const AdminActivityTable = ({ tableId, activities, activityTypes }) => {
  const history = useHistory();

  const onViewClick = (activity) => {
    switch (activity.action_type) {
      case activityTypes.RESCUE_CLAIM:
        return history.push(generatePath(routes.rescue, { rescueId: activity.rescue_id }));
      case activityTypes.RESCUE_ADOPT:
        return history.push(generatePath(routes.donation_edit, { donationId: activity.item_id }));
      case activityTypes.RESCUE_UNADOPT:
        return history.push(generatePath(routes.donation_edit, { donationId: activity.item_id }));
      case activityTypes.USER_MODIFIED:
        return history.push(generatePath(routes.userEdit, { id: activity.item_id }));
      case activityTypes.USER_ROLE_ATTACH:
        return history.push(generatePath(routes.userEdit, { id: activity.item_id }));
      case activityTypes.USER_ROLE_DETACH:
        return history.push(generatePath(routes.userEdit, { id: activity.item_id }));
      case activityTypes.RECEIVING_AGENCY_CREATED:
        return history.push(generatePath(routes.receiver, { id: activity.item_id, tab: 'profile' }));
      case activityTypes.RECEIVING_AGENCY_MODIFIED:
        return history.push(generatePath(routes.receiver, { id: activity.item_id, tab: 'profile' }));
      case activityTypes.DONOR_CREATED:
        return history.push(generatePath(routes.foodDonor, { foodDonorId: activity.item_id, tab: 'profile' }));
      case activityTypes.DONOR_MODIFIED:
        return history.push(generatePath(routes.foodDonor, { foodDonorId: activity.item_id, tab: 'profile' }));
      case activityTypes.DONATION_MODIFIED:
        return history.push(generatePath(routes.donation_edit, { donationId: activity.item_id }));
      default:
        break;
    }

    return history.push(generatePath(routes.dashboardAdmin));
  };

  const renderUserProfileLink = (user, id) => {
    if (!user) {
      return 'N/A';
    }

    return (
      <Box
        component={Link}
        sx={{ textDecoration: 'none', cursor: 'pointer', color: 'primary.main' }}
        to={generatePath(routes.userEditNew, { id })}
      >
        {user}
      </Box>
    );
  };

  const dateCustomBodyRender = (value) => {
    if (!value) {
      return '-';
    }
    const formattedDate = moment(value, 'YYYYMMDD').format('MM/DD/YYYY');

    return <span>{formattedDate}</span>;
  };

  const typeCustomBodyRender = (value) => value || 'N/A';

  const userCustomBodyRender = (value, tableMeta, csv = false) => {
    const activity = activities[getMuiTableDataIndex(tableMeta)];

    if (csv) {
      return value || '-';
    }

    return renderUserProfileLink(value, activity?.user_id);
  };

  const recordCustomBodyRender = (value, csv = false) => {
    if (csv) {
      return value || 'N/A';
    }

    if (value) {
      const parsed = deepParseJSON(value);
      const record = stringifyAndReplaceQuotes(parsed);

      return <pre>{record}</pre>;
    }

    return 'N/A';
  };

  const viewCustomBodyRender = (value, tableMeta) => {
    const activity = activities[getMuiTableDataIndex(tableMeta)];

    if (
      !activity ||
      activity?.action_type === activityTypes.MARKETING_HUB_ACCOUNT_LINKED ||
      activity?.action_type === activityTypes.MARKETING_HUB_FEED_ENGAGED ||
      activity?.action_type === activityTypes.USER_LOGGED_IN
    ) {
      return null;
    }

    return (
      <TableActionsButtonsWrapper>
        <TableActionButton title="View" icon={VisibilityIcon} onClick={() => onViewClick(activity)} />
      </TableActionsButtonsWrapper>
    );
  };

  return (
    <BaseMUIDataTable
      tableId={tableId}
      title="Activity"
      data={activities}
      columns={[
        {
          name: 'id',
          label: 'ID',
          options: {
            filter: false,
            sort: false,
            display: false,
          },
        },
        {
          name: 'created_at',
          label: 'Date',
          options: {
            sort: true,
            filter: false,
            customBodyRender: (value) => dateCustomBodyRender(value),
            customBodyRenderCSV: (value) => dateCustomBodyRender(value),
          },
        },
        {
          name: 'action_type',
          label: 'Type',
          options: {
            sort: true,
            filterType: 'multiselect',
            customFilterListOptions: {
              render: (value) => `Type: ${value}`,
            },
            customBodyRender: (value) => typeCustomBodyRender(value),
            customBodyRenderCSV: (value) => typeCustomBodyRender(value),
          },
        },
        {
          name: 'user',
          label: 'Initiating User',
          options: {
            sort: true,
            customFilterListOptions: {
              render: (value) => `User: ${value}`,
            },
            customBodyRender: (value, tableMeta) => userCustomBodyRender(value, tableMeta),
            customBodyRenderCSV: (value, tableMeta) => userCustomBodyRender(value, tableMeta, true),
          },
        },
        {
          name: 'changes',
          label: 'Affected Record',
          options: {
            sort: false,
            filter: false,
            customBodyRender: (value) => recordCustomBodyRender(value),
            customBodyRenderCSV: (value) => recordCustomBodyRender(value, true),
          },
        },
        {
          name: 'actions',
          label: 'View',
          options: {
            download: false,
            filter: false,
            sort: false,
            display: true,
            customBodyRender: (value, tableMeta) => viewCustomBodyRender(value, tableMeta),
          },
        },
      ]}
      options={{
        selectableRows: 'none',
        responsive: 'simple',
        downloadOptions: {
          filename: formatCsvFileName('Activities'),
        },
        sortOrder: {
          name: 'created_at',
          direction: 'desc',
        },
      }}
    />
  );
};

export default AdminActivityTable;
